import axios from 'axios';
import {
  ownerEmail,
  useViewAsOwner,
} from '@/shared/composition';
import { v4 as uuidv4 } from 'uuid';
import {
  REQUEST_ID_KEY,
  STATUS_NOT_AUTH,
} from '@/shared/constants/system';
import emitter from '@/shared/services/emitter';
import * as Sentry from '@sentry/vue';
import { emojiEliminateRequestInterceptor } from '@/shared/lib/emojiEliminate/interceptor';
import { axiosBitrix } from '@/shared/plugins/axiosBitrix';
import { useSentryAsync } from '@/shared/composition/useSentryAsync';

const { captureExceptionSentry } = useSentryAsync();

const TOKEN_MIN_VALIDITY = 70000;
// Количество ошибок авторизации при отправке запроса на сервис
let authFailsCount = 0;

function updateHeaders(token: string) {
  if (token) {
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    axiosBitrix.defaults.headers.common.Authorization = `Bearer ${token}`;
  }
}

const { axiosRequestInterceptor } = useViewAsOwner();

export default function(keycloak: any) {
  axios.defaults.headers.common.Accept = 'application/json';
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  updateHeaders(keycloak.token);

  axios.interceptors.request.use(
    config => {
      if (config.headers) {
        config.headers[REQUEST_ID_KEY] = uuidv4();
        config.headers['x-source-lp'] = ownerEmail.value ? 'personalAreaPartnerLP' : 'personalAreaClientLP';
      }

      axiosRequestInterceptor(config);
      emojiEliminateRequestInterceptor(config);
      return config;
    },
  );

  axios.interceptors.response.use(
    response => response,
    error => {
      const errorClone = JSON.parse(JSON.stringify(error));
      if (error.status === 0 || error.message.includes('Network')) {
        Sentry.captureMessage('Ошибка со статусом 0.');
      }
      if (errorClone.status === STATUS_NOT_AUTH) {
        authFailsCount += 1;
        if (authFailsCount === 1) {
          emitter.emit('createToast', {
            message: 'Произошла ошибка, попробуйте еще раз.',
            color: 'error',
          });
          Sentry.captureMessage(`Ошибка авторизации. текущее время: ${new Date().getTime()}, токен валиден до: ${keycloak.tokenParsed.exp}, timeSkew: ${keycloak.timeSkew}`);
          updateToken(keycloak);
          return false;
        } else if (authFailsCount > 1) {
          const logoutUrl = keycloak.createLogoutUrl();
          window.location.replace(logoutUrl);
        }
      }
      return Promise.reject(error);
    },
  );

  // обновление токена
  scheduleUpdateToken(keycloak);
}

function scheduleUpdateToken(keycloak: any) {
  try {
    if (keycloak.isSynthetic) {
      return;
    }
    const valid = (Math.round(
      keycloak.tokenParsed.exp +
        keycloak.timeSkew -
        new Date().getTime() / 1000,
    ) - 60) * 1000;
    setTimeout(() => updateToken(keycloak), valid > 0 ? valid : 0);
  } catch (error) {
    captureExceptionSentry(error, 'Не удалось запланировать обновление токена');
  }
}

async function updateToken(keycloak: any) {
  try {
    const refreshed = await keycloak.updateToken(TOKEN_MIN_VALIDITY);

    if (refreshed) {
      updateHeaders(keycloak.token);
    }

    scheduleUpdateToken(keycloak);
  } catch (error) {
    captureExceptionSentry(error, 'обновление токена кейклок');
  }
}
